import { useTranslation } from 'react-i18next';
import { Row, Col, Button, CardBody } from 'reactstrap';
import Select from 'react-select';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { InputActionMeta, SelectedData } from 'types/common/FormTypes';
import { transformOptionsToListsArray } from 'utils/common/handleSelect';
import { CheckboxOption } from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectOption';

import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { useDispatch } from 'react-redux';

import { MultiSelectSingleLineStyles } from 'styles/common/multiSelectSingleLineStyle';
import { CustomBranchMenu } from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectBranchMenu';

import { CustomerSearchFilters } from 'types/customer/customerTypes';
import { CustomerSearchValidationSchema } from './CustomerSearchValidation';
import { setPageFilters } from 'slices/customer/reducer';
import TextSelectedItems from 'Components/pos2u/TextSelectedItems';
import CustomerAutoComplete from 'Components/pos2u/Inputs/CustomerAutoComplete';
import {
  CustomerESSearchParams,
  getSearchCustomersFromES,
} from 'services/customerService';

interface CustomerSearchFormProps {
  isDarkMode: boolean;
  onSubmit: (
    value: CustomerSearchFilters,
    latestSearchPersonIds?: string[] | null
  ) => void;
  onSearchPersonIdsChange?: (personIds: string[] | null) => void;
  selectedCheckBoxDelete: number;
}

const MIN_ES_SEARCH_LENGTH = 3;
const ES_SEARCH_DEBOUNCE_MS = 1000;

type AutoCompleteField = keyof Pick<
  CustomerSearchFilters,
  'accountNumber' | 'name' | 'phoneNumber' | 'email'
>;

const buildCustomerESFilters = (
  values: Pick<
    CustomerSearchFilters,
    'accountNumber' | 'name' | 'phoneNumber' | 'email'
  >
): CustomerESSearchParams => ({
  ...(values.name.trim() ? { name: values.name.trim() } : {}),
  ...(values.phoneNumber.trim() ? { phone: values.phoneNumber.trim() } : {}),
  ...(values.accountNumber.trim()
    ? { account_number: values.accountNumber.trim() }
    : {}),
  ...(values.email.trim() ? { email: values.email.trim() } : {}),
});

const CustomerSearchForm = ({
  isDarkMode,
  onSubmit,
  onSearchPersonIdsChange,
  selectedCheckBoxDelete,
}: CustomerSearchFormProps) => {
  const { t, i18n } = useTranslation();
  const infoState = useSelector((state: RootState) => state.Info);
  const { branches } = infoState;
  const customerState = useSelector((state: RootState) => state.Customer);
  const { PageFilters } = customerState;

  const dispatch = useDispatch();

  const formik = useFormik<CustomerSearchFilters>({
    initialValues: PageFilters,
    validationSchema: CustomerSearchValidationSchema(t),
    enableReinitialize: true,
    onSubmit: (values) => {
      dispatch(setPageFilters(values));
      onSubmit(values, latestSearchPersonIdsRef.current);
    },
  });

  const { touched, validateForm } = formik;
  useEffect(() => {
    if (i18n.language && (touched.email || touched.phoneNumber)) {
      validateForm();
    }
  }, [i18n.language, touched.email, touched.phoneNumber, validateForm]);

  const [inputValue, setInputValue] = useState('');
  const [draftAutoCompleteValues, setDraftAutoCompleteValues] =
    useState<CustomerSearchFilters>({
      branch: PageFilters.branch,
      accountNumber: PageFilters.accountNumber,
      phoneNumber: PageFilters.phoneNumber,
      email: PageFilters.email,
      name: PageFilters.name,
    });
  const latestSearchPersonIdsRef = useRef<string[] | null>(null);
  const autocompleteRequestRef = useRef(0);

  const submitCustomerSearch = useCallback(
    (
      values: CustomerSearchFilters,
      personIds = latestSearchPersonIdsRef.current
    ) => {
      onSubmit(values, personIds);
      dispatch(setPageFilters(values));
    },
    [dispatch, onSubmit]
  );

  const handleInputChange = (newValue: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setInputValue(newValue);
    }
  };

  const handleChangeBranch = (selectedOptions: SelectedData[]) => {
    formik
      .setFieldValue(`branch`, transformOptionsToListsArray(selectedOptions))
      .then(async () => formik.setFieldTouched(`branch`, true));
  };

  useEffect(() => {
    setDraftAutoCompleteValues({
      branch: formik.values.branch,
      accountNumber: formik.values.accountNumber,
      phoneNumber: formik.values.phoneNumber,
      email: formik.values.email,
      name: formik.values.name,
    });
  }, [
    formik.values.accountNumber,
    formik.values.branch,
    formik.values.email,
    formik.values.name,
    formik.values.phoneNumber,
  ]);

  const currentESFilters = useMemo<CustomerESSearchParams>(
    () =>
      buildCustomerESFilters({
        accountNumber: draftAutoCompleteValues.accountNumber,
        name: draftAutoCompleteValues.name,
        phoneNumber: draftAutoCompleteValues.phoneNumber,
        email: draftAutoCompleteValues.email,
      }),
    [
      draftAutoCompleteValues.accountNumber,
      draftAutoCompleteValues.email,
      draftAutoCompleteValues.name,
      draftAutoCompleteValues.phoneNumber,
    ]
  );

  const hasSearchTextWithMinLength = useMemo(
    () =>
      [
        draftAutoCompleteValues.accountNumber.trim(),
        draftAutoCompleteValues.name.trim(),
        draftAutoCompleteValues.phoneNumber.trim(),
        draftAutoCompleteValues.email.trim(),
      ].some((text) => text.length >= MIN_ES_SEARCH_LENGTH),
    [
      draftAutoCompleteValues.accountNumber,
      draftAutoCompleteValues.email,
      draftAutoCompleteValues.name,
      draftAutoCompleteValues.phoneNumber,
    ]
  );

  useEffect(() => {
    const hasFilters = Object.keys(currentESFilters).length > 0;

    if (!hasFilters || !hasSearchTextWithMinLength) {
      latestSearchPersonIdsRef.current = null;
      return;
    }

    const requestId = ++autocompleteRequestRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const response = await getSearchCustomersFromES(currentESFilters);

        if (requestId !== autocompleteRequestRef.current) return;

        const latestSearchPersonIds = response.data?.total_results?.list ?? [];
        latestSearchPersonIdsRef.current = latestSearchPersonIds;
      } catch (error) {
        console.error('Failed to fetch customer autocomplete options:', error);

        if (requestId !== autocompleteRequestRef.current) return;

        latestSearchPersonIdsRef.current = [];
      }
    }, ES_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [currentESFilters, hasSearchTextWithMinLength]);

  const syncDraftToFormik = useCallback(async () => {
    const updates = (
      [
        ['accountNumber', draftAutoCompleteValues.accountNumber],
        ['name', draftAutoCompleteValues.name],
        ['phoneNumber', draftAutoCompleteValues.phoneNumber],
        ['email', draftAutoCompleteValues.email],
      ] as const
    ).filter(([field, nextValue]) => formik.values[field] !== nextValue);

    await Promise.all(
      updates.map(([field, nextValue]) =>
        formik.setFieldValue(field, nextValue, false)
      )
    );
  }, [draftAutoCompleteValues, formik]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextValues = {
      ...formik.values,
      accountNumber: draftAutoCompleteValues.accountNumber,
      name: draftAutoCompleteValues.name,
      phoneNumber: draftAutoCompleteValues.phoneNumber,
      email: draftAutoCompleteValues.email,
    };

    void syncDraftToFormik();
    submitCustomerSearch(nextValues);
  };

  const handleAutoCompleteInputChange = useCallback(
    (field: AutoCompleteField, nextValue: string) => {
      setDraftAutoCompleteValues((prev) => ({
        ...prev,
        [field]: nextValue,
      }));
    },
    []
  );

  const handleAutoCompleteBlur = useCallback(
    (field: AutoCompleteField) => {
      formik.setFieldTouched(field, true);
      const draftValue = draftAutoCompleteValues[field];

      if (formik.values[field] !== draftValue) {
        formik.setFieldValue(field, draftValue, false);
      }
    },
    [draftAutoCompleteValues, formik]
  );

  const handleAutoCompleteOptionSelect = useCallback(
    (
      field: AutoCompleteField,
      option: { value: string; label: string } | null,
      totalResultIds: string[]
    ) => {
      const nextValue = option?.value || '';
      const nextValues = {
        ...formik.values,
        [field]: nextValue,
      };
      const ids = option ? totalResultIds : null;

      latestSearchPersonIdsRef.current = ids;
      onSearchPersonIdsChange?.(ids);
      setDraftAutoCompleteValues((prev) => ({
        ...prev,
        [field]: nextValue,
      }));
      // ห้าม await — ต้อง commit ids + PageFilters พร้อมกันใน tick เดียว
      // ไม่งั้น fetchAllCustomers จะรันตอน PageFilters ยังไม่อัปเดต (ดูฝั่ง product)
      void formik.setValues(nextValues, false);
      submitCustomerSearch(nextValues, ids);
    },
    [formik, onSearchPersonIdsChange, submitCustomerSearch]
  );

  const marginStyle = useMemo(() => {
    return `me-2 mb-2`;
  }, []);
  const gapStyle = useMemo(() => {
    return `g-0`;
  }, []);
  const errorTextStyle = {};

  return (
    <Row className="mb-3">
      <CardBody className="border border-dashed border-end-0 border-start-0 my-0">
        <form
          onSubmit={handleSubmit}
          data-testid="customerSearchForm"
          noValidate
        >
          <Row className={`${gapStyle}`}>
            <Col md={10} lg={10}>
              <Row className={`${gapStyle}`}>
                <Col md={4} lg={4} className="col-xxl-4 ">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-branch"
                  >
                    <Select
                      id="branch"
                      name="branch"
                      aria-label="branch"
                      value={branches.filter(
                        (option) =>
                          Array.isArray(formik.values.branch) &&
                          formik.values.branch.includes(option.value)
                      )}
                      isMulti={true}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      inputValue={inputValue}
                      onInputChange={handleInputChange}
                      onChange={handleChangeBranch}
                      onBlur={() => {
                        formik.setFieldTouched('branch', true);
                      }}
                      options={branches}
                      components={{
                        Menu: CustomBranchMenu,
                        Option: CheckboxOption,
                      }}
                      styles={MultiSelectSingleLineStyles(isDarkMode)}
                      placeholder={t('Branch')}
                    />
                  </div>
                </Col>

                <Col md={4} lg={4} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-accountNumber"
                  >
                    <CustomerAutoComplete
                      t={t}
                      id="accountNumber"
                      name="accountNumber"
                      searchType="account_number"
                      allValues={currentESFilters}
                      onOptionSelect={(option, totalResultIds) => {
                        void handleAutoCompleteOptionSelect(
                          'accountNumber',
                          option,
                          totalResultIds
                        );
                      }}
                      onInputValueChange={(nextValue) => {
                        handleAutoCompleteInputChange(
                          'accountNumber',
                          nextValue
                        );
                      }}
                      value={formik.values.accountNumber}
                      onBlur={() => {
                        handleAutoCompleteBlur('accountNumber');
                      }}
                      isDarkMode={isDarkMode}
                      placeholder={t('AccountNumber')}
                    />
                  </div>
                </Col>
                <Col md={4} lg={4} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-name"
                  >
                    <CustomerAutoComplete
                      t={t}
                      id="name"
                      name="name"
                      searchType="name"
                      allValues={currentESFilters}
                      onOptionSelect={(option, totalResultIds) => {
                        void handleAutoCompleteOptionSelect(
                          'name',
                          option,
                          totalResultIds
                        );
                      }}
                      onInputValueChange={(nextValue) => {
                        handleAutoCompleteInputChange('name', nextValue);
                      }}
                      value={formik.values.name}
                      onBlur={() => {
                        handleAutoCompleteBlur('name');
                      }}
                      isDarkMode={isDarkMode}
                      placeholder={t('Name')}
                    />
                  </div>
                </Col>
              </Row>
              <Row className={`${gapStyle}`}>
                <Col md={4} lg={4} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-phoneNumber"
                  >
                    <CustomerAutoComplete
                      t={t}
                      id="phoneNumber"
                      name="phoneNumber"
                      searchType="phone"
                      allValues={currentESFilters}
                      onOptionSelect={(option, totalResultIds) => {
                        void handleAutoCompleteOptionSelect(
                          'phoneNumber',
                          option,
                          totalResultIds
                        );
                      }}
                      onInputValueChange={(nextValue) => {
                        handleAutoCompleteInputChange('phoneNumber', nextValue);
                      }}
                      value={formik.values.phoneNumber || ''}
                      onBlur={() => {
                        handleAutoCompleteBlur('phoneNumber');
                      }}
                      isDarkMode={isDarkMode}
                      placeholder={`${t(
                        'Phone Number'
                      )} e.g. +66812345678 or 0812345678`}
                    />
                  </div>

                  <div className="text-danger" style={errorTextStyle}>
                    {formik.touched.phoneNumber && formik.errors.phoneNumber
                      ? formik.errors.phoneNumber
                      : ''}
                  </div>
                </Col>
                <Col md={4} lg={4} className="col-xxl-4">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-email"
                  >
                    <CustomerAutoComplete
                      t={t}
                      id="email"
                      name="email"
                      searchType="email"
                      allValues={currentESFilters}
                      onOptionSelect={(option, totalResultIds) => {
                        void handleAutoCompleteOptionSelect(
                          'email',
                          option,
                          totalResultIds
                        );
                      }}
                      onInputValueChange={(nextValue) => {
                        handleAutoCompleteInputChange('email', nextValue);
                      }}
                      value={formik.values.email}
                      onBlur={() => {
                        handleAutoCompleteBlur('email');
                      }}
                      isDarkMode={isDarkMode}
                      placeholder={t('Email')}
                    />
                  </div>

                  <div className="text-danger" style={errorTextStyle}>
                    {formik.touched.email && formik.errors.email
                      ? formik.errors.email
                      : ''}
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={2} lg={2}>
              <div className="d-flex mx-lg-2 mx-md-0">
                <Button
                  color="success"
                  type="submit"
                  outline
                  data-testid="search-form-btn-submit"
                >
                  {t('Search')}
                </Button>
              </div>
            </Col>
          </Row>
        </form>
        {selectedCheckBoxDelete && selectedCheckBoxDelete > 0 ? (
          <TextSelectedItems selectedItems={selectedCheckBoxDelete} />
        ) : null}
      </CardBody>
    </Row>
  );
};

export default CustomerSearchForm;
